import React from 'react';
import { Link, useParams } from 'react-router-dom';

// Public Terms & Conditions page (/terms and /:tenantSlug/terms), linked from
// the login screen and the registration forms, and used as the store-listing
// terms URL. This is a VERBATIM rendering of docs/TERMS_AND_CONDITIONS.md
// (mirrored in the mobile repo) — same clause numbers, same wording. When the
// document changes, change both together.
const LAST_UPDATED = '23 July 2026';

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100 mb-3">{title}</h2>
    <div className="space-y-3 text-sm leading-relaxed text-gray-700 dark:text-slate-300">{children}</div>
  </section>
);

const Sub = ({ title, children }) => (
  <div className="mt-4">
    <h3 className="text-base font-semibold text-gray-900 dark:text-slate-100 mb-2">{title}</h3>
    <div className="space-y-3">{children}</div>
  </div>
);

const TermsAndConditions = () => {
  const { tenantSlug } = useParams();
  const loginPath = tenantSlug ? `/${tenantSlug}/login` : '/login';

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-slate-950 py-10 px-4">
      <div className="max-w-3xl mx-auto bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 shadow-sm">
        <div className="px-8 py-6 border-b border-gray-200 dark:border-slate-700">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Terms and Conditions</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">Last updated: {LAST_UPDATED}</p>
          <Link to={loginPath} className="mt-3 inline-block text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:underline">
            ← Back to sign in
          </Link>
        </div>

        <div className="px-8 py-8">
          <Section title="1. About us and acceptance of these terms">
            <p>
              1.1 These Terms and Conditions ("<strong>Terms</strong>") govern your relationship with{' '}
              <strong>Koniza Financial Services</strong> ("<strong>we</strong>", "<strong>us</strong>",
              "<strong>the Lender</strong>"), a registered microlender under the Microlending Act, 2018 (Act
              No. 7 of 2018) of the Republic of Namibia, supervised by the Namibia Financial Institutions
              Supervisory Authority ("<strong>NAMFISA</strong>"), registration number <strong>25/11/2052</strong>,
              with its principal place of business at <strong>ERF 6239, Samuel Shikomba Street, Katutura East,
              Windhoek, Namibia</strong>.
            </p>
            <p>
              1.2 They apply to all of our services, however you access them — at our offices, through our
              online client portal, through our mobile applications, or through any other channel we make
              available (together the "<strong>Services</strong>").
            </p>
            <p>
              1.3 By registering with us, applying for a loan, or accessing or using any of the Services, you
              ("<strong>you</strong>", "<strong>the Client</strong>") agree to be bound by these Terms. If you
              do not agree, do not use the Services.
            </p>
            <p>
              1.4 <strong>Each loan is additionally governed by its own written loan agreement</strong> (the
              "<strong>Loan Agreement</strong>"), signed by you electronically or in person. If these Terms
              conflict with a signed Loan Agreement, the Loan Agreement prevails for that loan.
            </p>
          </Section>

          <Section title="2. Eligibility and your account">
            <p>2.1 You must be at least 18 years old, legally capable of contracting, and resident in Namibia.</p>
            <p>
              2.2 Where you register for online access, a valid email address is required and must be verified.
              New accounts are activated after review by our staff. We may decline or deactivate an account at
              our discretion, subject to applicable law.
            </p>
            <p>
              2.3 You warrant that all information you provide to us — personal details, identity/passport
              number, contact and address details, employment and salary information, banking details, and
              supporting documents (identification, payslip, bank statement, proof of residence, photograph) —
              is true, accurate, current and complete, and that you will keep it updated.
            </p>
            <p>
              2.4 You are responsible for keeping your access credentials confidential. Sign-in to the Services
              is protected by two-factor authentication (a one-time code sent to your email). Where you choose
              to enable a device unlock feature (such as fingerprint or facial recognition) on your own device,
              that feature is provided and processed entirely by your device; we never receive, store or have
              access to your biometric data, and you remain responsible for who can unlock your device.
            </p>
            <p>
              2.5 Anything done through your account after successful authentication is deemed done by you,
              unless you have reported unauthorised access to us without delay.
            </p>
          </Section>

          <Section title="3. Our services">
            <p>
              3.1 We provide microlending services in terms of the Microlending Act, 2018, including receiving
              and assessing loan applications, concluding Loan Agreements, disbursing loans, and administering
              loans until settlement. Through our digital channels you may also maintain your client profile,
              submit supporting documents, review and electronically sign agreements, view your loans,
              instalments, invoices, penalties and statements, and receive notices and notifications.
            </p>
            <p>
              3.2 <strong>Our digital channels do not process payments.</strong> Repayments are made through the
              methods stated in your Loan Agreement (including debit order where you have authorised one) and
              are recorded by our staff. Balances displayed in the Services are for information; the amounts
              lawfully owing are determined by your Loan Agreement and our books of account.
            </p>
            <p>
              3.3 Nothing in the Services constitutes financial advice. Granting of credit is at our discretion
              and subject to our assessment and applicable law.
            </p>
          </Section>

          <Section title="4. Loan agreements and electronic signatures">
            <p>
              4.1 Submitting an application does not create a loan. A loan exists only once a Loan Agreement is
              concluded between you and us.
            </p>
            <p>
              4.2 You consent to conclude Loan Agreements and related consents (including the Client Information
              Consent Agreement and, where applicable, a Debit Order Consent Agreement){' '}
              <strong>electronically</strong>. You agree that your electronic signature has the same force and
              effect as a handwritten signature, and that documents signed this way, together with our system
              records, are valid evidence of the agreement.
            </p>
            <p>
              4.3 Before signing you will be presented with the full agreement, including the principal,
              interest rate, term, instalments, and statutory charges (currently the NAMFISA levy of 1.03% of
              the loan base and stamp duty of N$5.00, as amended from time to time). Do not sign unless you have
              read and understood it.
            </p>
          </Section>

          <Section title="5. Interest, fees and overdue amounts">
            <p>
              5.1 Interest, statutory levies, stamp duty and any permitted charges are those disclosed in your
              Loan Agreement, within the maxima allowed by the Microlending Act, 2018 and the Usury Act.
            </p>
            <p>
              5.2 If an instalment is not paid when due, overdue interest and penalties may accrue as set out in
              your Loan Agreement and permitted by law, and will be reflected in your invoices and statements.
              We may take lawful steps to recover overdue amounts, including handing the matter to attorneys or
              debt-collection agents; recoverable legal costs are as provided in your Loan Agreement and
              applicable law.
            </p>
          </Section>

          <Section title="6. Cooling-off and early repayment">
            <p>
              6.1 As provided by section 26(2) of the Microlending Act, 2018, you may terminate a Loan Agreement
              within the statutory cooling-off period by written notice and by repaying the amounts contemplated
              by the Act.
            </p>
            <p>6.2 You may repay a loan early in accordance with the Loan Agreement and applicable law.</p>
          </Section>

          <Section title="7. Personal information: collection, use and sharing">
            <p>
              7.1 <strong>What we collect.</strong> The information described in clause 2.3, together with your
              signatures, loan and repayment history, correspondence, notification preferences and, where you
              use our digital channels, technical data needed to deliver them (such as a device notification
              token).
            </p>
            <p>
              7.2 <strong>Why we use it.</strong> To verify your identity and assess your applications; to
              conclude, administer and enforce Loan Agreements; to comply with the Microlending Act,
              financial-intelligence/anti-money-laundering and other laws; to submit statutory returns and
              reports to NAMFISA; to communicate with you; to maintain the security of the Services; and to keep
              the records the law requires us to keep.
            </p>
            <p>
              7.3 <strong>Who we share it with.</strong> With your consent given in the Client Information
              Consent Agreement and as permitted by law, we may share your information with:
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>registered credit bureaus</strong> — to obtain your credit record and payment history and to report the conduct of your account;</li>
              <li><strong>NAMFISA and other regulators or authorities</strong> — where reporting or disclosure is required by law;</li>
              <li><strong>banks and payment service providers</strong> — to process disbursements and debit orders you have authorised;</li>
              <li><strong>our service providers</strong> — such as hosting, communications and notification providers who process information on our behalf under confidentiality obligations;</li>
              <li><strong>attorneys, debt-collection agents and courts</strong> — where necessary to enforce a Loan Agreement or defend a claim;</li>
              <li><strong>our professional advisers and auditors</strong> — under confidentiality obligations.</li>
            </ul>
            <p>
              7.4 <strong>We do not sell your personal information</strong>, and we do not share it for
              third-party marketing.
            </p>
            <p>
              7.5 <strong>Retention and security.</strong> We retain your information for as long as required
              for the purposes above and by law, and protect it with appropriate technical and organisational
              measures. In particular, records of concluded Loan Agreements — signed contracts, invoices,
              statements and repayment history — are retained for <strong>5 years after the loan is
              settled</strong>, as required of a registered microlender under Namibian law (including the
              Microlending Act, 2018 and the Financial Intelligence Act, 2012), and are deleted when that
              period ends. Audit and regulatory reporting records already submitted to NAMFISA are retained as
              required by law. Retained records are stored securely, are not used for marketing, and are not
              sold or shared except as required by law. You may request access to or correction of your
              information using the contact details in clause 13.
            </p>
            <p>
              7.6 <strong>Account and data deletion.</strong> You may request deletion of your account and
              personal data at any time by emailing <strong>info@konizafs.com</strong> with the subject line{' '}
              <strong>"Account Deletion Request"</strong>, sent from the email address registered on your
              account (include your full name and, if known, your client code so we can verify the request). An
              account with an unsettled loan can only be deleted once the loan is settled. On a verified
              request we deactivate your account immediately and, within <strong>30 days</strong>, delete: your
              login account; your client profile (personal, employment and banking details); uploaded documents
              that do not form part of a concluded Loan Agreement; and device notification tokens and
              notification history. Records described in clause 7.5 are kept for their statutory retention
              period and then deleted. We confirm completion of the deletion by email.
            </p>
          </Section>

          <Section title="8. Communications">
            <p>
              8.1 You consent to receive service communications from us by email, telephone, and — where you use
              our digital channels — in-portal messages and push notifications (loan status, signatures
              required, invoices, arrears and similar). You can disable push notifications in your device
              settings; statutory and contractual notices may still be sent by email or other lawful means.
            </p>
          </Section>

          <Section title="9. Acceptable use">
            <p>
              You must not: provide false information or documents; use another person's identity; interfere
              with, reverse engineer, or attempt to gain unauthorised access to the Services or other clients'
              data; use the Services for any unlawful purpose; or upload malicious content. We may suspend or
              terminate access for breach.
            </p>
          </Section>

          <Section title="10. Availability, disclaimers and liability">
            <p>
              10.1 We aim to keep our digital channels available but do not guarantee uninterrupted, error-free
              operation. They may be suspended for maintenance, upgrades or reasons beyond our control; our
              offices remain available during business hours.
            </p>
            <p>
              10.2 The Services are provided "as is" to the maximum extent permitted by law. We are not liable
              for loss caused by inaccurate information you supplied, your device or network, unauthorised
              access resulting from your failure to protect your credentials or device, or indirect or
              consequential loss. Nothing in these Terms excludes liability that cannot be excluded under
              Namibian law, or limits your statutory rights under the Microlending Act, 2018.
            </p>
            <p>
              10.3 Information displayed in our digital channels does not replace the signed Loan Agreement,
              invoices or statements issued by us; in case of discrepancy, clause 3.2 applies.
            </p>
          </Section>

          <Section title="11. Suspension and termination">
            <p>
              11.1 You may stop using our digital channels at any time; doing so does not affect your
              obligations under any Loan Agreement.
            </p>
            <p>
              11.2 We may suspend or close your online access for breach of these Terms, suspected fraud, legal
              requirement, or extended inactivity. Your Loan Agreements survive any closure of online access.
            </p>
          </Section>

          <Section title="12. Complaints and disputes">
            <p>
              12.1 Complaints should first be raised with us at <strong>info@konizafs.com</strong> /{' '}
              <strong>+264 81 866 9826</strong>. We will investigate and respond.
            </p>
            <p>
              12.2 If you are not satisfied, you may refer the complaint to <strong>NAMFISA</strong>, as
              contemplated in the Microlending Act, 2018.
            </p>
          </Section>

          <Section title="13. Contact">
            <p>
              <strong>Koniza Financial Services</strong> · NAMFISA Reg. No. 25/11/2052
              <br />
              Physical address: ERF 6239, Samuel Shikomba Street, Katutura East, Windhoek, Namibia
              <br />
              Email: info@konizafs.com · Phone: +264 81 866 9826
            </p>
          </Section>

          <Section title="14. General">
            <p>
              14.1 <strong>Governing law:</strong> these Terms and all use of the Services are governed by the
              laws of the Republic of Namibia, and the Namibian courts have jurisdiction, subject to clause 12.
            </p>
            <p>
              14.2 We may amend these Terms from time to time. The current version will be published with its
              "last updated" date; material changes will be notified to you. Continued use after the effective
              date constitutes acceptance.
            </p>
            <p>
              14.3 If any provision is unenforceable, the remainder stays in force. Our failure to enforce a
              right is not a waiver.
            </p>
          </Section>

          <Section title="15. Mobile application: licence, device permissions and marketplaces">
            <p>
              Where you access the Services through our mobile application (obtained from a third-party
              application marketplace), the following also applies:
            </p>

            <Sub title="15.1 Licence">
              <p>
                We grant you a personal, non-exclusive, non-transferable, revocable licence to install and use
                the application on a device you own or control, solely to access the Services in accordance with
                these Terms and the applicable marketplace's usage rules.
              </p>
            </Sub>

            <Sub title="15.2 Device permissions and access">
              <p>
                The application may request access to the following device features. Each permission is
                optional: you may grant, withhold or revoke it at any time in your device settings, but the
                related feature will then be unavailable.
              </p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li><strong>Notifications</strong> — to alert you to loan status changes, required signatures, invoices, arrears and similar service messages;</li>
                <li><strong>Camera</strong> — to photograph documents and capture your client photo for KYC;</li>
                <li><strong>Photos / files and storage</strong> — to select and upload supporting documents, and to open or save documents such as contracts, invoices and statements;</li>
                <li><strong>Fingerprint / face recognition (device unlock)</strong> — to unlock your session on your device; processed entirely on your device, never transmitted to us (clause 2.4);</li>
                <li><strong>Internet / network access</strong> — to communicate securely with our systems; standard network charges from your mobile operator or internet provider are your responsibility.</li>
              </ul>
              <p>The application does not access your contacts, call logs, messages or precise location.</p>
            </Sub>

            <Sub title="15.3 Updates">
              <p>
                We may release updates that change, add or remove features, and may require you to install the
                latest version to keep using the application, including for security reasons. Your device's
                marketplace settings control automatic installation of updates.
              </p>
            </Sub>

            <Sub title="15.4 Google Play">
              <p>
                Where the application was obtained from Google Play, your use is also subject to the Google Play
                Terms of Service. These Terms are between you and us, not Google; Google has no obligation to
                provide support or maintenance and bears no responsibility for the application or these Terms.
              </p>
            </Sub>

            <Sub title="15.5 Apple App Store">
              <p>
                Where the application was obtained from the Apple App Store: (a) these Terms are concluded
                between you and us only — not with Apple Inc. ("Apple") — and we, not Apple, are solely
                responsible for the application and its content; (b) your licence is limited to use on
                Apple-branded devices you own or control, as permitted by the App Store Terms of Service
                (including Family Sharing); (c) Apple has no obligation to furnish any maintenance or support;
                (d) if the application fails to conform to an applicable warranty you may notify Apple, and
                Apple will refund any purchase price to you — to the maximum extent permitted by law Apple has
                no other warranty obligation, and any other claims, losses, liabilities, damages, costs or
                expenses attributable to a failure to conform to a warranty are our responsibility; (e) we, not
                Apple, are responsible for addressing any claims by you or a third party relating to the
                application or your possession or use of it, including product liability claims, claims of
                failure to conform to legal or regulatory requirements, and claims under consumer-protection or
                similar legislation; (f) in the event of a third-party claim that the application or your
                possession and use of it infringes intellectual property rights, we, not Apple, are solely
                responsible for the investigation, defence, settlement and discharge of the claim; (g) you
                represent and warrant that you are not located in a country subject to a U.S. Government embargo
                or designated as a "terrorist supporting" country, and that you are not on any U.S. Government
                list of prohibited or restricted parties; (h) Apple, and Apple's subsidiaries, are third-party
                beneficiaries of these Terms as they relate to the application and, upon your acceptance, have
                the right to enforce them against you; (i) you must comply with applicable third-party terms of
                agreement (for example, your wireless data agreement) when using the application.
              </p>
            </Sub>
          </Section>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
